import React, { useState, useCallback, useMemo } from "react";
import {
  Box,
  Typography,
  Stack,
  FormControl,
  Select,
  MenuItem,
  LinearProgress,
  CircularProgress,
} from "@mui/material";
import { useSnackbar } from "notistack";
import CloudUploadRoundedIcon from "@mui/icons-material/CloudUploadRounded";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import { BulkImportUsers } from "../../hooks/useUserData";
import { GetUserGroupsByOrganisationId } from "../../hooks/useUserGroupdata";
import { GetCustomers } from "../../hooks/useCustomerData";

const INK = "#0a1628";
const INK_MUTED = "#5f6877";
const LINE = "#e6e4df";
const LINE_SOFT = "#eeece7";
const BRAND = "#8a1125";
const NAVY = "#10324a";

const SAMPLE_CSV = `first_name,last_name,email,phone
Johan,Andersson,johan.andersson@example.com,0701234567
Maria,Eriksson,maria.eriksson@example.com,0709876543
Erik,Lindberg,erik.lindberg@example.com,0705551234`;

const SectionLabel = ({ index, children }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, mb: 2 }}>
    <Typography
      sx={{
        fontFamily: '"Outfit", sans-serif',
        fontSize: "11px",
        fontWeight: 600,
        color: BRAND,
        letterSpacing: "0.1em",
        fontVariantNumeric: "tabular-nums",
      }}
    >
      {index}
    </Typography>
    <Box sx={{ width: 14, height: "1px", background: LINE }} />
    <Typography
      sx={{
        fontSize: "10.5px",
        fontWeight: 700,
        letterSpacing: "0.24em",
        textTransform: "uppercase",
        color: INK_MUTED,
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      {children}
    </Typography>
  </Box>
);

const FieldLabel = ({ children, required }) => (
  <Typography
    sx={{
      fontSize: "11px",
      fontWeight: 600,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
      color: INK_MUTED,
      mb: 1,
      fontFamily: '"DM Sans", sans-serif',
    }}
  >
    {children}
    {required && (
      <Box component="span" sx={{ color: BRAND, ml: 0.5 }}>
        *
      </Box>
    )}
  </Typography>
);

const menuPaperProps = {
  PaperProps: {
    sx: {
      mt: 1,
      borderRadius: "12px",
      border: `1px solid ${LINE}`,
      boxShadow: "0 24px 48px -24px rgba(10,22,40,0.18)",
      "& .MuiMenuItem-root": {
        fontSize: "14px",
        fontFamily: '"DM Sans", sans-serif',
        py: 1.25,
        px: 2,
        "&.Mui-selected": {
          background: "rgba(10,22,40,0.04)",
          "&:hover": { background: "rgba(10,22,40,0.06)" },
        },
      },
    },
  },
};

const selectSx = {
  height: 48,
  background: "#fff",
  fontSize: "15px",
  fontWeight: 500,
  fontFamily: '"DM Sans", sans-serif',
  color: INK,
  "& .MuiSelect-select": {
    py: 0,
    display: "flex",
    alignItems: "center",
  },
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: LINE,
    borderRadius: "12px",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.2)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(10,22,40,0.32)",
    borderWidth: "1px",
  },
  "& .MuiSelect-icon": { color: INK_MUTED },
  "&.Mui-disabled": { opacity: 0.5 },
};

const ResultChip = ({ icon: Icon, label, tone }) => {
  const styles =
    tone === "success"
      ? {
          fg: "#1a6536",
          bg: "rgba(43,140,77,0.1)",
          border: "rgba(43,140,77,0.32)",
          dot: "#2b8c4d",
          halo: "rgba(43,140,77,0.18)",
        }
      : {
          fg: BRAND,
          bg: "rgba(138,17,37,0.08)",
          border: "rgba(138,17,37,0.32)",
          dot: BRAND,
          halo: "rgba(138,17,37,0.18)",
        };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        px: 1.75,
        height: 32,
        borderRadius: "999px",
        border: `1px solid ${styles.border}`,
        background: styles.bg,
        color: styles.fg,
        fontSize: "13px",
        fontWeight: 700,
        letterSpacing: "0.005em",
        fontFamily: '"DM Sans", sans-serif',
      }}
    >
      <Box
        sx={{
          width: 9,
          height: 9,
          borderRadius: "50%",
          background: styles.dot,
          boxShadow: `0 0 0 4px ${styles.halo}`,
          flexShrink: 0,
        }}
      />
      <Icon sx={{ fontSize: 16, opacity: 0.95, flexShrink: 0 }} />
      {label}
    </Box>
  );
};

export const BulkImportUsersModal = ({ closeModal }) => {
  const { enqueueSnackbar } = useSnackbar();

  const [selectedOrganisation, setSelectedOrganisation] = useState("");
  const [selectedGroup, setSelectedGroup] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const { data: customerList } = GetCustomers();
  const { data: userGroupData } = GetUserGroupsByOrganisationId(
    selectedOrganisation
  );

  const organisations = useMemo(
    () =>
      (customerList?.data || []).map((org) => ({
        id: org.id,
        label: org.organisation_name || `Organisation ${org.id}`,
      })),
    [customerList]
  );

  const groups = useMemo(
    () =>
      (userGroupData?.data || []).map((g) => ({
        id: g.id,
        label: g.group_name,
      })),
    [userGroupData]
  );

  const bulkImportMutation = BulkImportUsers({
    onSuccess: (response) => {
      const result = response.data;
      setImportResult(result);

      if (result.success) {
        enqueueSnackbar(`${result.created} användare importerade!`, {
          variant: "success",
        });
      } else if (result.created > 0) {
        enqueueSnackbar(
          `${result.created} användare importerade, ${result.failed} misslyckades`,
          { variant: "warning" }
        );
      } else {
        enqueueSnackbar("Importen misslyckades. Se felmeddelanden nedan.", {
          variant: "error",
        });
      }
    },
    onError: (error) => {
      const message =
        error.response?.data?.message || "Ett fel uppstod vid importen";
      enqueueSnackbar(message, { variant: "error" });
      if (error.response?.data?.errors) {
        setImportResult(error.response.data);
      }
    },
  });

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setDragActive(false);

      const file = e.dataTransfer.files?.[0];
      if (file && file.name.endsWith(".csv")) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        enqueueSnackbar("Endast CSV-filer är tillåtna", { variant: "error" });
      }
    },
    [enqueueSnackbar]
  );

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setImportResult(null);
    }
  };

  const handleImport = () => {
    if (!selectedOrganisation) {
      enqueueSnackbar("Välj en organisation först", { variant: "warning" });
      return;
    }
    if (!selectedGroup) {
      enqueueSnackbar("Välj en grupp först", { variant: "warning" });
      return;
    }
    if (!selectedFile) {
      enqueueSnackbar("Välj en CSV-fil först", { variant: "warning" });
      return;
    }
    const formData = new FormData();
    formData.append("file", selectedFile);
    formData.append("organisation_id", selectedOrganisation);
    formData.append("group_id", selectedGroup);
    bulkImportMutation.mutate(formData);
  };

  const downloadSampleCSV = () => {
    const blob = new Blob([SAMPLE_CSV], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "users_template.csv";
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const resetForm = () => {
    setSelectedFile(null);
    setSelectedOrganisation("");
    setSelectedGroup("");
    setImportResult(null);
  };

  const loading = bulkImportMutation.isLoading;
  const canImport =
    !!selectedFile && !!selectedOrganisation && !!selectedGroup && !loading;

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <SectionLabel index="01">Destination</SectionLabel>
        <Stack spacing={2.25}>
          <Box>
            <FieldLabel required>Organisation</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={selectedOrganisation}
                onChange={(e) => {
                  setSelectedOrganisation(e.target.value);
                  setSelectedGroup("");
                }}
                displayEmpty
                disabled={loading}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        Välj organisation
                      </Box>
                    );
                  return (
                    organisations.find((o) => o.id === v)?.label || ""
                  );
                }}
              >
                <MenuItem value="" disabled>
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Välj organisation
                  </Box>
                </MenuItem>
                {organisations.map((o) => (
                  <MenuItem key={o.id} value={o.id}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box>
            <FieldLabel required>Grupp</FieldLabel>
            <FormControl fullWidth>
              <Select
                value={selectedGroup}
                onChange={(e) => setSelectedGroup(e.target.value)}
                displayEmpty
                disabled={!selectedOrganisation || loading}
                IconComponent={KeyboardArrowDownRoundedIcon}
                MenuProps={menuPaperProps}
                sx={selectSx}
                renderValue={(v) => {
                  if (!v)
                    return (
                      <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                        {selectedOrganisation
                          ? "Välj grupp"
                          : "Välj organisation först"}
                      </Box>
                    );
                  return groups.find((g) => g.id === v)?.label || "";
                }}
              >
                <MenuItem value="" disabled>
                  <Box sx={{ color: INK_MUTED, fontStyle: "italic" }}>
                    Välj grupp
                  </Box>
                </MenuItem>
                {groups.map((g) => (
                  <MenuItem key={g.id} value={g.id}>
                    {g.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
      </Box>

      <Box sx={{ mb: 4 }}>
        <SectionLabel index="02">CSV-fil</SectionLabel>
        <Box
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() =>
            !loading && document.getElementById("csv-file-input").click()
          }
          sx={{
            border: `2px dashed ${
              dragActive
                ? "rgba(138,17,37,0.5)"
                : selectedFile
                ? "rgba(43,140,77,0.32)"
                : LINE
            }`,
            borderRadius: "16px",
            p: 4,
            textAlign: "center",
            background: dragActive
              ? "rgba(138,17,37,0.04)"
              : selectedFile
              ? "rgba(43,140,77,0.04)"
              : "linear-gradient(180deg, rgba(255,255,255,0.6), rgba(250,248,245,0.6))",
            cursor: loading ? "not-allowed" : "pointer",
            transition: "all 0.3s cubic-bezier(0.22,1,0.36,1)",
            opacity: loading ? 0.5 : 1,
            "&:hover": !loading && {
              borderColor: "rgba(138,17,37,0.32)",
              background: "rgba(138,17,37,0.03)",
            },
          }}
        >
          <input
            id="csv-file-input"
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            style={{ display: "none" }}
            disabled={loading}
          />
          <Box
            sx={{
              width: 56,
              height: 56,
              mx: "auto",
              mb: 2,
              borderRadius: "16px",
              background: selectedFile
                ? "linear-gradient(135deg, #2b8c4d, #1a6536)"
                : `linear-gradient(135deg, ${NAVY}, #1a4a6e)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: selectedFile
                ? "0 16px 32px -16px rgba(43,140,77,0.45)"
                : "0 16px 32px -16px rgba(16,50,74,0.45)",
              transition: "all 0.35s ease",
            }}
          >
            {selectedFile ? (
              <CheckCircleRoundedIcon sx={{ color: "#fff", fontSize: 26 }} />
            ) : (
              <CloudUploadRoundedIcon sx={{ color: "#fff", fontSize: 26 }} />
            )}
          </Box>
          <Typography
            sx={{
              fontFamily: '"Outfit", sans-serif',
              fontSize: "17px",
              fontWeight: 600,
              color: INK,
              letterSpacing: "-0.01em",
              mb: 0.5,
              wordBreak: "break-all",
            }}
          >
            {selectedFile
              ? selectedFile.name
              : "Dra och släpp en CSV-fil här"}
          </Typography>
          <Typography
            sx={{
              fontSize: "12.5px",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
            }}
          >
            {selectedFile
              ? "Klicka för att välja en annan fil"
              : "eller klicka för att välja · Max 5 MB, max 1000 rader"}
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ mt: 2 }}>
            <LinearProgress
              sx={{
                height: 4,
                borderRadius: 2,
                background: "rgba(10,22,40,0.06)",
                "& .MuiLinearProgress-bar": {
                  background: BRAND,
                  borderRadius: 2,
                },
              }}
            />
            <Typography
              sx={{
                mt: 1,
                fontSize: "11.5px",
                fontWeight: 600,
                letterSpacing: "0.14em",
                textTransform: "uppercase",
                color: INK_MUTED,
                fontFamily: '"DM Sans", sans-serif',
              }}
            >
              Importerar användare...
            </Typography>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.75,
          p: 2,
          mb: 4,
          borderRadius: "12px",
          background: "rgba(16,50,74,0.04)",
          border: `1px solid rgba(16,50,74,0.12)`,
        }}
      >
        <InfoRoundedIcon sx={{ fontSize: 18, color: NAVY, mt: "2px" }} />
        <Box>
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 600,
              color: INK,
              fontFamily: '"DM Sans", sans-serif',
              lineHeight: 1.5,
            }}
          >
            CSV-filen måste ha kolumnerna{" "}
            <Box component="span" sx={{ color: BRAND, fontWeight: 700 }}>
              first_name, last_name, email, phone
            </Box>
          </Typography>
          <Typography
            sx={{
              mt: 0.5,
              fontSize: "12px",
              color: INK_MUTED,
              fontFamily: '"DM Sans", sans-serif',
              lineHeight: 1.5,
            }}
          >
            Ladda ner mallen nedan för att komma igång snabbt.
          </Typography>
        </Box>
      </Box>

      {importResult && (
        <Box sx={{ mb: 4 }}>
          <SectionLabel index="03">Resultat</SectionLabel>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 2 }}>
            <ResultChip
              icon={CheckCircleRoundedIcon}
              label={`${importResult.created || 0} skapade`}
              tone="success"
            />
            {importResult.failed > 0 && (
              <ResultChip
                icon={ErrorRoundedIcon}
                label={`${importResult.failed} misslyckades`}
                tone="error"
              />
            )}
          </Stack>

          {importResult.errors?.length > 0 && (
            <Box
              sx={{
                border: `1px solid ${LINE}`,
                borderRadius: "12px",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: "60px 120px 1fr",
                  gap: 2,
                  px: 2,
                  py: 1.25,
                  background: "rgba(10,22,40,0.025)",
                  borderBottom: `1px solid ${LINE_SOFT}`,
                }}
              >
                {["Rad", "Fält", "Felmeddelande"].map((h) => (
                  <Typography
                    key={h}
                    sx={{
                      fontSize: "10px",
                      fontWeight: 700,
                      letterSpacing: "0.18em",
                      textTransform: "uppercase",
                      color: INK_MUTED,
                      fontFamily: '"DM Sans", sans-serif',
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>
              <Box sx={{ maxHeight: 260, overflow: "auto" }}>
                {importResult.errors.map((error, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "60px 120px 1fr",
                      gap: 2,
                      px: 2,
                      py: 1.25,
                      borderBottom:
                        index < importResult.errors.length - 1
                          ? `1px solid ${LINE_SOFT}`
                          : "none",
                      fontSize: "13px",
                      fontFamily: '"DM Sans", sans-serif',
                      color: INK,
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontVariantNumeric: "tabular-nums",
                        color: INK_MUTED,
                      }}
                    >
                      {error.row || "—"}
                    </Typography>
                    <Typography
                      sx={{ fontSize: "13px", fontWeight: 600, color: INK }}
                    >
                      {error.field}
                    </Typography>
                    <Typography sx={{ fontSize: "13px", color: INK_MUTED }}>
                      {error.message}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          pt: 3,
          borderTop: `1px solid ${LINE_SOFT}`,
          flexWrap: "wrap",
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={downloadSampleCSV}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            gap: 1,
            px: 2,
            py: 1.25,
            background: "transparent",
            color: INK,
            border: `1px solid ${LINE}`,
            borderRadius: "10px",
            fontFamily: '"DM Sans", sans-serif',
            fontSize: "13px",
            fontWeight: 600,
            cursor: "pointer",
            transition: "all 0.25s ease",
            "&:hover": {
              background: "rgba(10,22,40,0.025)",
              borderColor: "rgba(10,22,40,0.22)",
            },
          }}
        >
          <DownloadRoundedIcon sx={{ fontSize: 16 }} />
          Ladda ner mall
        </Box>

        <Box sx={{ display: "flex", gap: 1.25, flexWrap: "wrap" }}>
          {importResult && (
            <Box
              component="button"
              type="button"
              onClick={resetForm}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 1,
                px: 2,
                py: 1.25,
                background: "transparent",
                color: INK_MUTED,
                border: `1px solid ${LINE}`,
                borderRadius: "10px",
                fontFamily: '"DM Sans", sans-serif',
                fontSize: "13px",
                fontWeight: 600,
                cursor: "pointer",
                transition: "all 0.25s ease",
                "&:hover": {
                  color: INK,
                  borderColor: "rgba(10,22,40,0.22)",
                },
              }}
            >
              <RestartAltRoundedIcon sx={{ fontSize: 16 }} />
              Ny import
            </Box>
          )}
          <Box
            component="button"
            type="button"
            onClick={closeModal}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.25,
              py: 1.25,
              background: "transparent",
              color: INK_MUTED,
              border: `1px solid ${LINE}`,
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: "pointer",
              transition: "all 0.25s ease",
              "&:hover": {
                color: INK,
                borderColor: "rgba(10,22,40,0.22)",
              },
            }}
          >
            <CloseRoundedIcon sx={{ fontSize: 16 }} />
            Stäng
          </Box>
          <Box
            component="button"
            type="button"
            onClick={handleImport}
            disabled={!canImport}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              px: 2.75,
              py: 1.25,
              background: INK,
              color: "#fff",
              border: "none",
              borderRadius: "10px",
              fontFamily: '"DM Sans", sans-serif',
              fontSize: "13px",
              fontWeight: 600,
              cursor: canImport ? "pointer" : "not-allowed",
              opacity: canImport ? 1 : 0.4,
              transition: "all 0.25s ease",
              boxShadow: canImport
                ? "0 10px 24px -12px rgba(10,22,40,0.55)"
                : "none",
              "&:hover": canImport
                ? { background: "#1a2638", transform: "translateY(-1px)" }
                : {},
            }}
          >
            {loading ? (
              <CircularProgress size={14} sx={{ color: "#fff" }} />
            ) : (
              <CloudUploadRoundedIcon sx={{ fontSize: 16 }} />
            )}
            Importera
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default BulkImportUsersModal;
